import { useEffect, useMemo, useRef, useState, useCallback } from 'react'

import { QueryFunction, QueryKey, useQuery } from '@tanstack/react-query'
import debounce from 'lodash/debounce'
import get from 'lodash/get'
import uniqBy from 'lodash/uniqBy'

import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import { DEBOUNCE_TIMEOUT, MIN_SEARCH_CHARS } from 'configuration/typeahead'
import {
    DatadogActionName,
    useMonitorLoadTime,
} from 'features/Monitoring/Datadog'
import { TypeaheadOption } from 'types'

import useCerebroApiRequest from '../api/useCerebroApiRequest'

type ReturnValue<T extends TypeaheadOption = TypeaheadOption> = [
    T[],
    boolean,
    (value: any) => void,
    () => void,
    null | string,
]

export type ApiFuncResponse<T> = Promise<CerebroPaginatedResponse<T>>

interface Params<T, U = TypeaheadOption> {
    apiSearchFunc: (query: string) => ApiFuncResponse<T> | null | undefined
    optionFormatter: (option: T) => U
    defaultOptions?: T[]
    prefetchApiFunc?: () => ApiFuncResponse<T> | null | undefined
    prefetchQueryConfig?: null | {
        queryKey: QueryKey
        queryFn: QueryFunction
    }
    isPrefetchEnabled?: boolean
    resultsDataIndex?: string
    allowCreatingOptions?: boolean
}

/**
 * Hook that performs searches for typeahead options and optionally prefetches data
 *
 * IMPORTANT: when providing the `prefetchApiFunc` callback option, make sure this
 * callback function is wrapped in `React.useCallback`. This ensures the prefetch
 * function will only be executed on initial mount.
 */
function useTypeahead<T = object, U extends TypeaheadOption = TypeaheadOption>({
    apiSearchFunc,
    optionFormatter,
    defaultOptions = [],
    prefetchApiFunc,
    isPrefetchEnabled,
    prefetchQueryConfig = null,
    resultsDataIndex = 'data.results',
    allowCreatingOptions = false,
}: Params<T, U>): ReturnValue<U> {
    const mounted = useRef(false)
    const searchQuery = useRef<null | string>(null)
    const [prefetchResults, setPrefetchResults] = useState<T[]>([])
    const [prefetchLoading, setPrefetchLoading] = useState<boolean>(false)

    const [searchResults, setSearchResults] = useState<T[]>([])
    const [searchLoading, setSearchLoading] = useState<boolean>(false)
    const [showAllResults, setShowAllResults] = useState<boolean>(true)

    const makeCerebroApiRequest = useCerebroApiRequest()

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isPrefetchEnabled != false) {
            setSearchResults([])
            setShowAllResults(true)
            searchQuery.current = null
        }
    }, [isPrefetchEnabled])

    const { data: preResults, isFetching: isPrefetchLoading } = useQuery({
        queryKey: prefetchQueryConfig?.queryKey || [],
        queryFn: prefetchQueryConfig?.queryFn || (() => Promise.resolve(null)),
        ...(prefetchQueryConfig || {}),
        enabled: !!prefetchQueryConfig && isPrefetchEnabled != false,
    })

    useEffect(() => {
        if (preResults && mounted.current) {
            const results: T[] = get(preResults, resultsDataIndex, [])
            setPrefetchResults(results)
        }
        setPrefetchLoading(isPrefetchLoading)
    }, [preResults, isPrefetchLoading, resultsDataIndex])

    useEffect(() => {
        if (!prefetchQueryConfig && prefetchApiFunc) {
            const prefetch = async (): Promise<void> => {
                setPrefetchLoading(true)
                const apiFn = prefetchApiFunc()
                if (apiFn) {
                    const prefetchResponse = await makeCerebroApiRequest({
                        request: apiFn,
                    })
                    if (mounted.current) {
                        if (prefetchResponse) {
                            const results: T[] = get(
                                prefetchResponse,
                                resultsDataIndex,
                                []
                            )
                            setPrefetchResults(results)
                        }
                        setPrefetchLoading(false)
                    }
                }
            }
            prefetch()
        }
    }, [
        makeCerebroApiRequest,
        prefetchApiFunc,
        prefetchQueryConfig,
        resultsDataIndex,
    ])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debounceCallback = useCallback(
        debounce((call: () => Promise<void>) => {
            return call()
        }, DEBOUNCE_TIMEOUT),
        []
    )

    const performSearch = useCallback(
        async (query: string): Promise<void> => {
            const searchFn = apiSearchFunc(query)
            if (!searchFn) {
                setSearchLoading(false)
                return
            }

            setSearchLoading(true)
            setShowAllResults(false)
            const response = await makeCerebroApiRequest({
                request: searchFn,
            })
            if (searchQuery.current === query) {
                // only update the state if the user has not changed the input
                // to avoid overwriting the results with a stale or out of order response
                setSearchResults(get(response, resultsDataIndex, []))
                setSearchLoading(false)
            }
        },
        [apiSearchFunc, makeCerebroApiRequest, resultsDataIndex]
    )

    const search = useCallback(
        async (query: string): Promise<void> => {
            await debounceCallback(() => performSearch(query))
        },
        [debounceCallback, performSearch]
    )

    function handleChangeInput(value: string): void {
        searchQuery.current = value
        if (value.length < MIN_SEARCH_CHARS) {
            setSearchResults([])
            setShowAllResults(true)
            debounceCallback.cancel()
            setSearchLoading(false)
            return
        }

        setSearchLoading(true)
        setSearchResults([])
        search(value)
    }

    function resetLoadingState(): void {
        // Do not display loading state after the user has selected an option
        setSearchLoading(false)
        setPrefetchLoading(false)
    }

    const loading = searchLoading || prefetchLoading

    useMonitorLoadTime({
        actionName: DatadogActionName.LOAD_TYPEAHEAD,
        loading,
        typeaheadContext: {
            query: searchQuery.current,
        },
    })

    const results = useMemo(() => {
        const innerResults = uniqBy(
            [
                ...searchResults,
                ...(showAllResults
                    ? [...defaultOptions, ...prefetchResults]
                    : []),
            ].map(optionFormatter),
            'value'
        )

        if (
            allowCreatingOptions &&
            searchQuery.current &&
            !innerResults.some(
                (opt) =>
                    opt.label.toLowerCase() ===
                    searchQuery.current?.toLowerCase()
            )
        ) {
            innerResults.unshift({
                label: searchQuery.current,
                value: searchQuery.current,
                toBeCreated: true,
            } as U)
        }
        return innerResults
    }, [
        searchResults,
        showAllResults,
        defaultOptions,
        prefetchResults,
        optionFormatter,
        allowCreatingOptions,
    ])

    return [
        results,
        loading,
        handleChangeInput,
        resetLoadingState,
        searchQuery.current,
    ]
}

export default useTypeahead
